/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { FilterAltOff } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
  UltraCompanyChoices,
  UltraLabelingExplorerBoxStatus,
  UltraLabelingExplorerMode,
  UltraLabelingExplorerSort,
} from '@dai/graphql'
import { format, isValid, parseISO } from 'date-fns'

import { RangeFilter } from './explorerCards'
import { formatStatusLabel, formatUltraCompanyChoices } from './explorerUtils'
import {
  BOX_STATUSES,
  SORT_OPTIONS,
  SOURCE_CUSTOMERS,
  parseUpcs,
  type FilterState,
} from './explorerUrlParams'

const ExplorerFilterSidebar = ({
  mode,
  sort,
  filters,
  onSortChange,
  onSourceCustomerChange,
  onUpcsChange,
  onStatusesChange,
  onRangeChange,
  onCreatedAfterChange,
  onCreatedBeforeChange,
  onReset,
}: {
  mode: UltraLabelingExplorerMode
  sort: UltraLabelingExplorerSort
  filters: FilterState
  onSortChange: (value: string) => void
  onSourceCustomerChange: (value: string) => void
  onUpcsChange: (upcs: string[]) => void
  onStatusesChange: (statuses: UltraLabelingExplorerBoxStatus[]) => void
  onRangeChange: (
    minField: keyof FilterState,
    maxField: keyof FilterState,
    min: string,
    max: string,
  ) => void
  onCreatedAfterChange: (value: string) => void
  onCreatedBeforeChange: (value: string) => void
  onReset: () => void
}) => (
  <Paper
    sx={{
      p: 1.5,
      borderRadius: 1,
      width: { xs: '100%', lg: 244 },
      flexShrink: 0,
      position: { lg: 'sticky' },
      top: { lg: 16 },
    }}
  >
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: '1fr',
        },
        gap: 1.5,
      }}
    >
      <TextField
        select
        size="small"
        label="Sort by"
        value={sort}
        onChange={event => onSortChange(event.target.value)}
      >
        {SORT_OPTIONS.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Source customer"
        value={filters.sourceCustomer}
        onChange={event => onSourceCustomerChange(event.target.value)}
      >
        <MenuItem value="">All</MenuItem>
        {SOURCE_CUSTOMERS.map(customer => (
          <MenuItem key={customer} value={customer}>
            {formatUltraCompanyChoices(customer as UltraCompanyChoices)}
          </MenuItem>
        ))}
      </TextField>
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={parseUpcs(filters.upcs)}
        onChange={(_, value) => onUpcsChange(value)}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            label="UPCs"
            placeholder="Type UPC and press Enter"
          />
        )}
      />
      <TextField
        select
        SelectProps={{
          multiple: true,
          displayEmpty: true,
          renderValue: selected => {
            const s = selected as UltraLabelingExplorerBoxStatus[]
            return s.length === 0 ? 'Any' : s.map(formatStatusLabel).join(', ')
          },
          onChange: event => {
            const value = event.target.value as UltraLabelingExplorerBoxStatus[]
            onStatusesChange(value)
          },
        }}
        InputLabelProps={{ shrink: true }}
        size="small"
        label="Status"
        value={filters.statuses}
      >
        {BOX_STATUSES.map(status => (
          <MenuItem key={status} value={status}>
            <Checkbox
              checked={filters.statuses.includes(status)}
              size="small"
            />
            <ListItemText primary={formatStatusLabel(status)} />
          </MenuItem>
        ))}
      </TextField>
      <DatePicker
        label="From date"
        value={filters.createdAfter ? parseISO(filters.createdAfter) : null}
        onChange={date => {
          onCreatedAfterChange(
            date && isValid(date) ? format(date, 'yyyy-MM-dd') : '',
          )
        }}
        slotProps={{ textField: { size: 'small' } }}
      />
      <DatePicker
        label="To date"
        value={filters.createdBefore ? parseISO(filters.createdBefore) : null}
        onChange={date => {
          onCreatedBeforeChange(
            date && isValid(date) ? format(date, 'yyyy-MM-dd') : '',
          )
        }}
        slotProps={{ textField: { size: 'small' } }}
      />
      <Button
        variant="outlined"
        startIcon={<FilterAltOff />}
        onClick={onReset}
        sx={{ minHeight: 40 }}
      >
        Reset
      </Button>
    </Box>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(2, minmax(0, 1fr))',
          lg: '1fr',
        },
        gap: 1.5,
        mt: 1.5,
      }}
    >
      {mode === UltraLabelingExplorerMode.IMAGE && (
        <RangeFilter
          label="Unidentified count"
          min={0}
          max={20}
          step={1}
          minValue={filters.minUnidentifiedCount}
          maxValue={filters.maxUnidentifiedCount}
          onChange={(min, max) =>
            onRangeChange(
              'minUnidentifiedCount',
              'maxUnidentifiedCount',
              min,
              max,
            )
          }
        />
      )}
      <RangeFilter
        label="Image score"
        min={0}
        max={100}
        step={0.1}
        minValue={filters.minImageAcquisitionScore}
        maxValue={filters.maxImageAcquisitionScore}
        onChange={(min, max) =>
          onRangeChange(
            'minImageAcquisitionScore',
            'maxImageAcquisitionScore',
            min,
            max,
          )
        }
      />
      {mode === UltraLabelingExplorerMode.CROP && (
        <RangeFilter
          label="Crop score"
          min={0}
          max={100}
          step={0.1}
          minValue={filters.minBoxAcquisitionScore}
          maxValue={filters.maxBoxAcquisitionScore}
          onChange={(min, max) =>
            onRangeChange(
              'minBoxAcquisitionScore',
              'maxBoxAcquisitionScore',
              min,
              max,
            )
          }
        />
      )}
    </Box>
  </Paper>
)

export default ExplorerFilterSidebar
